import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';

function formatDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  return date.toLocaleString();
}

function formatPersonName(person) {
  if (!person || typeof person !== 'object') {
    return '-';
  }

  const fullName = [person.firstName, person.lastName]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');

  return fullName || String(person.email || '').trim() || '-';
}

function formatAction(action) {
  if (!action) {
    return '-';
  }

  if (typeof action === 'string') {
    return action;
  }

  return String(action.value || action.name || '-');
}

function SystemUserDetailsPageView({
  profileId,
  activeTab,
  tabs,
  userRow,
  isLoading,
  error,
  success,
  canManageAcl,
  availableRoles,
  assignedRoles,
  selectedRoleIds,
  isRolesLoading,
  isRolesSaving,
  rolesError,
  actionLogs,
  isActionLogsLoading,
  actionLogsError,
  setActiveTab,
  goBack,
  toggleAssignedRole,
  saveAssignedRoles,
}) {
  const { t } = useTranslation();

  return (
    <div>
      <FloatingAlerts error={error || rolesError || actionLogsError} success={success} />
      <div className="d-flex align-items-start justify-content-between gap-3">
        <PageHeader
          title={t('systemUsers.userDetails')}
          subtitle={profileId ? `${t('systemUsers.userDetailsSubtitle')} #${profileId}` : ''}
        />
        <button type="button" className="btn btn-outline-secondary" onClick={goBack}>
          {t('systemUsers.backToUsers')}
        </button>
      </div>

      <LoadingContainer isLoading={isLoading} hasData={Boolean(userRow)} initialText={t('common.loading')} className="">
        {userRow ? (
          <>
            <div className="card card-body mb-3">
              <div className="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between gap-3">
                <div>
                  <h5 className="mb-1">{userRow.fullName || '-'}</h5>
                  <div className="text-muted small">{userRow.email || '-'}</div>
                </div>
                <span className={`badge rounded-pill fw-semibold text-uppercase ${userRow.disabled ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'}`}>
                  {userRow.disabled ? t('systemUsers.statusDisabled') : t('systemUsers.statusActive')}
                </span>
              </div>
            </div>

            <ul className="nav nav-tabs mb-3">
              {tabs.map((tab) => (
                <li className="nav-item" key={tab.key}>
                  <button
                    type="button"
                    className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {t(tab.label)}
                  </button>
                </li>
              ))}
            </ul>

            {activeTab === 'info-acl' ? (
              <div className="card card-body">
                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-4">
                    <div className="text-muted small">{t('systemUsers.fieldName')}</div>
                    <div>{userRow.firstName || '-'}</div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="text-muted small">{t('systemUsers.fieldSurname')}</div>
                    <div>{userRow.lastName || '-'}</div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="text-muted small">{t('systemUsers.fieldUsername')}</div>
                    <div className="text-break">{userRow.email || '-'}</div>
                  </div>
                </div>

                {canManageAcl ? (
                  <div>
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                      <h5 className="mb-0">{t('systemUsers.rolesTitle')}</h5>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1 px-3"
                        onClick={saveAssignedRoles}
                        disabled={isRolesLoading || isRolesSaving}
                      >
                        <i className="ti ti-device-floppy" aria-hidden="true"></i>
                        {t('systemUsers.saveRoles')}
                      </button>
                    </div>
                    {rolesError ? <div className="text-danger small mb-2">{rolesError}</div> : null}
                    {isRolesLoading ? (
                      <div className="text-muted small">{t('common.loading')}</div>
                    ) : (
                      <>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {assignedRoles.length > 0 ? assignedRoles.map((role) => (
                            <span key={role.id} className="badge bg-light-primary text-primary">{role.name}</span>
                          )) : (
                            <span className="text-muted small">{t('systemUsers.noRolesAssigned')}</span>
                          )}
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {availableRoles.map((role) => (
                            <label key={role.id} className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedRoleIds.includes(role.id)}
                                onChange={() => toggleAssignedRole(role.id)}
                                disabled={isRolesSaving}
                              />
                              <span className="form-check-label">
                                <span className="d-block">{role.name}</span>
                                <span className="text-muted small">{role.slug}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {activeTab === 'action-log' ? (
              <div className="card card-body">
                <h5>{t('systemUsers.actionLogTitle')}</h5>
                {isActionLogsLoading ? <div className="text-muted small">{t('systemUsers.loadingActionLog')}</div> : null}
                {!isActionLogsLoading && !actionLogsError ? (
                  actionLogs.length === 0 ? (
                    <div className="text-muted small">{t('systemUsers.noActionLogs')}</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table search-table align-middle mb-0">
                        <thead className="header-item">
                          <tr>
                            <th style={{ width: '170px' }}>{t('systemUsers.actionLogCreated')}</th>
                            <th style={{ width: '220px' }}>{t('systemUsers.actionLogAction')}</th>
                            <th>{t('systemUsers.actionLogDetails')}</th>
                            <th style={{ width: '140px' }}>{t('systemUsers.actionLogIp')}</th>
                            <th style={{ width: '180px' }}>{t('systemUsers.actionLogOwner')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {actionLogs.map((item, index) => (
                            <tr key={`${item.created || item.createdAt || item.action || index}-${index}`}>
                              <td>{formatDateTime(item.created || item.createdAt || item.created_at)}</td>
                              <td>{formatAction(item.action)}</td>
                              <td className="text-break" style={{ whiteSpace: 'pre-wrap' }}>{item.details || '-'}</td>
                              <td>{item.ip || '-'}</td>
                              <td>{formatPersonName(item.owner)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </LoadingContainer>
    </div>
  );
}

export default SystemUserDetailsPageView;
